import React, { createContext, useContext, useEffect, useRef, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { refreshAuthToken, getTokenExpiryMs, clearAuth, isAuthenticated } from "@/lib/auth";
import { setShareThumbnailAccount } from '@/lib/share-thumbnail-cache';

interface User {
  id: number;
  email: string;
  username: string;
  role: 'admin' | 'team' | 'external' | 'guest';
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  isVirtual?: boolean;
  shortCode?: string | null;
  colorCode?: string | null;
  isProjectAssigned?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isRecoveryMode: boolean;
  refetchUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PROACTIVE_REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const proactiveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const scheduleProactiveRefresh = () => {
    if (proactiveTimerRef.current) {
      clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = undefined;
    }

    const remainingMs = getTokenExpiryMs();
    if (remainingMs === null) return;

    const refreshIn = Math.max(remainingMs - PROACTIVE_REFRESH_BUFFER_MS, 1000);
    console.log(`[Auth] Proactive refresh scheduled in ${Math.round(refreshIn / 1000)}s (token expires in ${Math.round(remainingMs / 1000)}s)`);

    proactiveTimerRef.current = setTimeout(async () => {
      console.log('[Auth] Proactive token refresh triggered');
      const success = await refreshAuthToken();
      if (success) {
        console.log('[Auth] Proactive refresh succeeded');
        queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
        scheduleProactiveRefresh();
      } else {
        console.warn('[Auth] Proactive refresh failed — session may have expired');
      }
    }, refreshIn);
  };

  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      try {
        // Auth rides on httpOnly cookies — sent automatically.
        const response = await fetch('/api/auth/me', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            const refreshed = isAuthenticated() && await refreshAuthToken();
            if (refreshed) {
              const retryResponse = await fetch('/api/auth/me', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
              });
              
              if (retryResponse.ok) {
                const userData = await retryResponse.json();
                return userData as User;
              }
            }
            
            clearAuth();
          }
          return null;
        }
        
        const userData = await response.json();
        return userData as User;
      } catch (error) {
        return null;
      }
    },
    retry: false,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 4,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (user) {
      scheduleProactiveRefresh();
    }
    return () => {
      if (proactiveTimerRef.current) {
        clearTimeout(proactiveTimerRef.current);
      }
    };
  }, [user?.id]);

  const isRecoveryMode = user?.id === -1 || user?.isVirtual === true;

  useEffect(() => {
    if (!isLoading) setShareThumbnailAccount(queryClient, user ? `${user.id}:${user.role}` : null);
  }, [queryClient, isLoading, user?.id, user?.role]);

  const contextValue: AuthContextType = {
    user: user || null,
    isLoading,
    isAuthenticated: !!user,
    isRecoveryMode,
    refetchUser: refetch,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
